// Prompt 23 B3 — admin-only Usage report over the billing_events ledger.
import { useCallback, useEffect, useState } from "react";
import { Download } from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import { formatMoney } from "../lib/usageFormat";

const FIELD = "border border-neutral-900 px-3 py-1.5 font-mono text-sm bg-white";

function Totals({ t }) {
  const items = [
    ["total delivered", t.total, "usage-total"],
    ["free tier", t.free_tier, "usage-free"],
    ["billable", t.billable, "usage-billable"],
    ["billable amount", formatMoney(t.amount_usd), "usage-amount"],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {items.map(([label, v, id]) => (
        <div key={id} className="card-flat p-5">
          <div className="label-mini">{label}</div>
          <div className="font-display text-3xl font-semibold mt-2" data-testid={id}>{v}</div>
        </div>
      ))}
    </div>
  );
}

export default function AdminUsage() {
  const [f, setF] = useState({ start: "", end: "", company_id: "all", source: "all" });
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const params = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") p.set(k, v); });
    return p.toString();
  }, [f]);

  useEffect(() => {
    api.get(`/admin/usage?${params()}`).then(({ data }) => { setData(data); setErr(null); })
      .catch((e) => { setData(null); setErr(e?.response?.data?.detail || "Could not load usage"); });
  }, [params]);

  const exportCsv = async () => {
    try {
      const r = await api.get(`/admin/usage.csv?${params()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lf_usage_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_e) {
      setErr("Could not export CSV");
    }
  };

  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  return (
    <Layout>
      <PageHeader eyebrow="admin" title="Usage" testid="admin-usage-header"
                  description={`Invoices delivered into receivers' GLs. Billable at ${formatMoney(data?.price_per_invoice_usd ?? 0.25)} each after the free tier. Nothing is charged yet.`}
                  actions={data && <button type="button" className="btn-primary flex items-center gap-2"
                                   onClick={exportCsv} data-testid="usage-export-csv">
                             <Download size={14} /> export csv</button>} />
      <div className="p-8 space-y-6" data-testid="admin-usage-page">
        {data ? <div className="card-flat p-4 flex flex-wrap items-end gap-4" data-testid="usage-filters">
          <label className="text-xs"><div className="label-mini mb-1">from</div>
            <input type="date" className={FIELD} value={f.start} onChange={set("start")} data-testid="usage-filter-start" /></label>
          <label className="text-xs"><div className="label-mini mb-1">to</div>
            <input type="date" className={FIELD} value={f.end} onChange={set("end")} data-testid="usage-filter-end" /></label>
          <label className="text-xs"><div className="label-mini mb-1">receiving company</div>
            <select className={FIELD} value={f.company_id} onChange={set("company_id")} data-testid="usage-filter-company">
              <option value="all">All companies</option>
              {(data?.companies || []).map((c) => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
            </select></label>
          <label className="text-xs"><div className="label-mini mb-1">source platform</div>
            <select className={FIELD} value={f.source} onChange={set("source")} data-testid="usage-filter-source">
              <option value="all">All sources</option>
              {(data?.sources || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select></label>
        </div> : null}
        {err ? <div className="card-flat p-4 text-sm text-red-700" data-testid="usage-error">{err}</div> : null}
        {data ? <Totals t={data.totals} /> : null}
        {data && data.free_tier_by_company.length > 0 ? (
          <section className="card-flat p-6" data-testid="usage-free-tier-table">
            <div className="label-mini">free tier by receiving company · 100 invoices or 6 months, whichever comes first</div>
            <table className="w-full text-sm mt-3">
              <thead><tr className="text-left label-mini">
                <th className="py-2">company</th><th>invoices used of 100</th><th>days left</th><th>status</th><th>stripe billing</th>
              </tr></thead>
              <tbody>
                {data.free_tier_by_company.map((c) => (
                  <tr key={c.company_id} className="border-t border-neutral-200" data-testid={`usage-ft-${c.company_id}`}>
                    <td className="py-2">{c.company_name}</td><td>{c.used} / {c.limit}</td><td>{c.days_left}</td>
                    <td>{c.active ? "active" : c.ended_by === "invoices" ? "ended · 100 used" : "ended · 6 months"}</td>
                    <td data-testid={`usage-stripe-${c.company_id}`}>
                      {c.stripe_billing_status === "no_stripe_customer" ? "no stripe customer"
                        : `${(c.stripe_billing_status || "—").replace("_", " ")}${c.has_payment_method ? " · pm on file" : " · no pm"}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}
        {data ? <section className="card-flat p-6" data-testid="usage-daily-table">
          <div className="label-mini">per day</div>
          {data && data.daily.length === 0 ? (
            <p className="text-sm text-neutral-500 mt-3" data-testid="usage-empty">No deliveries match these filters.</p>
          ) : (
            <table className="w-full text-sm mt-3">
              <thead><tr className="text-left label-mini">
                <th className="py-2">date</th><th>delivered</th><th>free tier</th><th>billable</th><th>amount</th>
              </tr></thead>
              <tbody>
                {(data?.daily || []).map((d) => (
                  <tr key={d.date} className="border-t border-neutral-200" data-testid={`usage-row-${d.date}`}>
                    <td className="py-2 font-mono">{d.date}</td><td>{d.total}</td><td>{d.free_tier}</td>
                    <td>{d.billable}</td><td>{formatMoney(d.amount_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section> : null}
      </div>
    </Layout>
  );
}
